/**
 * GameController — orchestrates TMDB fetching, pool expansion, guess resolution,
 * node placement, hint logic, and win detection.
 *
 * Framework-agnostic: emits events for the UI layer to react to.
 * The same controller is used for solo and multiplayer — the difference
 * is whether state updates are broadcast via WebSocket.
 */

import { tmdb, imgUrl } from './tmdb.js';
import { isValidMovie, isValidTv } from './validation.js';
import { AnswerPool } from './answer-pool.js';
import { Board } from './board.js';
import { getDefaultExcludedGenres } from './genres.js';
import { isActorNode, isMovieNode, isTvNode, isMediaNode, parseNodeId } from './constants.js';
import { findSteinerTree } from './steiner.js';
import { saveAutosave, clearAutosave } from './storage.js';

export class GameController {
  constructor(options = {}) {
    this.targets = options.targets || [];
    this.settings = {
      excludedGenres: options.excludedGenres || getDefaultExcludedGenres(),
      crewConnections: options.crewConnections || false,
      scoring: options.scoring || 'standard',
      timeLimit: options.timeLimit || 0,
      hintMode: options.hintMode || 'unlimited',
      hintPool: options.hintPool || 5,
      banList: options.banList || { actors: [], titles: [] },
      pathConstraints: options.pathConstraints || {},
      connectionPreview: options.connectionPreview !== false,
      isExploration: options.isExploration || false,
    };

    this.pool = new AnswerPool(this.settings.excludedGenres);
    this.board = null;
    this.hintsUsed = 0;
    this.hintsRemaining = this.settings.hintMode === 'finite' ? this.settings.hintPool : Infinity;
    this.won = false;
    this.bestPath = null;
    this.bestResult = null;
    this.guessHistory = [];
    this.startTime = null;
    this.elapsedTime = 0;

    // Event listeners
    this._listeners = new Map();

    // Target node IDs
    this._targetNodeIds = new Set(this.targets.map(t => {
      if (typeof t === 'string') return t;
      return (t.type === 'actor' ? 'a' : t.type === 'movie' ? 'm' : 't') + t.id;
    }));
  }

  /** Initialize the board and fetch initial target data */
  async init(containerEl) {
    this.board = new Board(containerEl, this._targetNodeIds);
    this.board.init();
    this.startTime = Date.now();

    // Fetch and expand pool for each target
    const fetchPromises = this.targets.map(async (target) => {
      try {
        const tmdbData = await this._fetchTmdbForTarget(target);
        if (!tmdbData) return;

        const nodeId = typeof target === 'string' ? target : this._nodeIdFromTarget(target);
        const { prefix } = parseNodeId(nodeId);

        // Expand the pool
        this._expandPool(prefix, tmdbData);

        // Extract node info and add target to board
        const nodeInfo = this._extractNodeInfo(prefix, nodeId, tmdbData);
        this.board.addNode(nodeId, {
          label: nodeInfo.label,
          image: nodeInfo.image,
          type: prefix === 'a' ? 'actor' : 'media',
          genres: nodeInfo.genres,
          year: this._extractYear(tmdbData),
        });
        this.pool.markFound(nodeId);

        // Store target name for UI
        target._resolvedName = nodeInfo.label;
        target._resolvedImage = nodeInfo.image;
      } catch (e) {
        console.error('Failed to fetch target:', target, e);
      }
    });

    await Promise.all(fetchPromises);
    this.board.fit();
    this._emit('initialized');
    this._autosave();
  }

  /** Process a guess */
  async processGuess(input) {
    if (!input || !input.trim()) return { success: false, reason: 'empty' };

    const trimmed = input.trim();

    // Check ban list
    if (this._isBanned(trimmed)) {
      const result = { success: false, reason: 'banned', input: trimmed };
      this.guessHistory.push(result);
      this._emit('guess', result);
      return result;
    }

    // In exploration mode, try TMDB search directly
    if (this.settings.isExploration) {
      return this._processExplorationGuess(trimmed);
    }

    // Resolve against the pool
    const matches = this.pool.resolve(trimmed);

    if (matches.length === 0) {
      const result = { success: false, reason: 'no-match', input: trimmed };
      this.guessHistory.push(result);
      this._emit('guess', result);
      return result;
    }

    // Find the best match that connects to the board
    for (const match of matches) {
      if (this.pool.isFound(match.nodeId)) {
        const result = { success: false, reason: 'already-found', input: trimmed, label: match.label };
        this.guessHistory.push(result);
        this._emit('guess', result);
        return result;
      }

      // Check path constraints
      if (!this._meetsConstraints(match)) {
        const result = { success: false, reason: 'constraint', input: trimmed, label: match.label };
        this.guessHistory.push(result);
        this._emit('guess', result);
        return result;
      }

      // Add to board
      const addResult = await this.addNodeToBoard(match.nodeId);
      if (addResult) {
        const result = { success: true, nodeId: match.nodeId, label: match.label, type: match.type };
        this.guessHistory.push(result);
        this._emit('guess', result);
        this._checkWin();
        this._autosave();
        return result;
      }
    }

    const result = { success: false, reason: 'no-connection', input: trimmed };
    this.guessHistory.push(result);
    this._emit('guess', result);
    return result;
  }

  /** Add a node to the board (fetch data, expand pool, create edges) */
  async addNodeToBoard(nodeId, options = {}) {
    if (!nodeId || this.board.hasNode(nodeId)) return false;

    const { prefix, numId } = parseNodeId(nodeId);

    try {
      const tmdbData = await this._fetchTmdb(prefix, numId);
      if (!tmdbData) return false;

      const nodeInfo = this._extractNodeInfo(prefix, nodeId, tmdbData);

      // Find connections to existing board nodes
      const boardNodeIds = this.board.getNodeIds();
      const relatedIds = new Set(nodeInfo.relatedNodeIds);
      const connections = boardNodeIds.filter(id => relatedIds.has(id));

      // In non-exploration mode, must connect to the board (unless it's a target)
      if (!this.settings.isExploration && connections.length === 0 && !this._targetNodeIds.has(nodeId)) {
        return false;
      }

      // Add node and edges
      this.board.addNodeWithConnections(nodeId, {
        label: nodeInfo.label,
        image: nodeInfo.image,
        type: prefix === 'a' ? 'actor' : 'media',
        genres: nodeInfo.genres,
        year: this._extractYear(tmdbData),
      }, connections);

      this.pool.markFound(nodeId);

      // Expand the pool with new data
      this._expandPool(prefix, tmdbData);

      if (options.isHint) {
        this.board.markHint(nodeId, options.hintSource);
      }

      this._emit('nodeAdded', { nodeId, label: nodeInfo.label, type: prefix === 'a' ? 'actor' : 'media' });

      return true;
    } catch (e) {
      console.error('Failed to add node:', nodeId, e);
      return false;
    }
  }

  /** Get connection preview for a potential guess */
  getConnectionPreview(input) {
    if (!this.settings.connectionPreview || !input) return [];
    const matches = this.pool.resolve(input);
    if (matches.length === 0) return [];

    const boardNodeIds = new Set(this.board.getNodeIds());
    const previews = [];

    for (const match of matches) {
      if (this.pool.isFound(match.nodeId)) continue;
      // We'd need the node's related IDs which require TMDB fetch
      // For preview, use the pool's stored credit data
      previews.push({ nodeId: match.nodeId, label: match.label, type: match.type });
    }

    return previews;
  }

  // --- Hint Methods ---

  async hintMediaFromActor(actorId) {
    if (this.hintsRemaining <= 0) return null;

    try {
      const numId = typeof actorId === 'string' ? parseInt(actorId.replace(/^a/, ''), 10) : actorId;
      const p = await tmdb(`/person/${numId}`, { append_to_response: 'combined_credits' });
      const credits = (p.combined_credits?.cast || [])
        .filter(c => {
          if (c.media_type === 'movie') return isValidMovie(c, this.settings.excludedGenres) && !this.pool.isFound('m' + c.id);
          if (c.media_type === 'tv') return isValidTv(c, this.settings.excludedGenres) && !this.pool.isFound('t' + c.id);
          return false;
        })
        .sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0));

      if (!credits.length) return null;

      const best = credits[0];
      const nid = (best.media_type === 'movie' ? 'm' : 't') + best.id;

      if (best.media_type === 'movie') {
        this.pool.registerMovie({ id: best.id, title: best.title, poster_path: best.poster_path, vote_count: best.vote_count || 0 });
      } else {
        this.pool.registerTv({ id: best.id, name: best.name, poster_path: best.poster_path, vote_count: best.vote_count || 0 });
      }

      this.hintsUsed++;
      if (this.settings.hintMode === 'finite') this.hintsRemaining--;

      await this.addNodeToBoard(nid, { isHint: true, hintSource: 'a' + numId });
      this._checkWin();
      this._autosave();
      this._emit('hint', { type: 'media-from-actor', nodeId: nid, label: best.title || best.name });
      return best.title || best.name;
    } catch (e) {
      console.error('hintMediaFromActor error:', e);
      return null;
    }
  }

  async hintActorFromMedia(mediaNodeId) {
    if (this.hintsRemaining <= 0) return null;

    try {
      const isMovie = mediaNodeId.startsWith('m');
      const numId = parseInt(mediaNodeId.replace(/^(m|t)/, ''), 10);
      let cast;

      if (isMovie) {
        const d = await tmdb(`/movie/${numId}`, { append_to_response: 'credits' });
        cast = d.credits?.cast || [];
      } else {
        const d = await tmdb(`/tv/${numId}`, { append_to_response: 'aggregate_credits' });
        cast = d.aggregate_credits?.cast || [];
      }

      const unfound = cast.filter(c => c.profile_path && !this.pool.isFound('a' + c.id));
      if (!unfound.length) return null;

      const best = unfound[0]; // first in cast = highest billed
      const nid = 'a' + best.id;

      this.pool.registerActor({ id: best.id, name: best.name, profile_path: best.profile_path });

      this.hintsUsed++;
      if (this.settings.hintMode === 'finite') this.hintsRemaining--;

      await this.addNodeToBoard(nid, { isHint: true, hintSource: mediaNodeId });
      this._checkWin();
      this._autosave();
      this._emit('hint', { type: 'actor-from-media', nodeId: nid, label: best.name });
      return best.name;
    } catch (e) {
      console.error('hintActorFromMedia error:', e);
      return null;
    }
  }

  async hintBridge() {
    if (this.hintsRemaining <= 0) return null;

    // Find an actor in fetched data that connects two unconnected board nodes
    const boardNodeIds = this.board.getNodeIds();
    const boardMediaIds = boardNodeIds.filter(id => isMediaNode(id));

    // Get cast for each media node from the pool's credit data
    const mediaCasts = new Map();
    for (const mid of boardMediaIds) {
      const cast = this.pool._mediaCredits.get(mid);
      if (cast) {
        mediaCasts.set(mid, new Set(cast.map(c => c.id)));
      }
    }

    // Find actors that appear in 2+ media nodes that aren't already connected
    const actorMedia = new Map(); // actorId -> Set of mediaNodeIds
    for (const [mid, actorIds] of mediaCasts) {
      for (const aid of actorIds) {
        if (this.pool.isFound('a' + aid)) continue;
        if (!actorMedia.has(aid)) actorMedia.set(aid, new Set());
        actorMedia.get(aid).add(mid);
      }
    }

    // Find bridging actors (connect 2+ media)
    let bestActor = null;
    let bestCount = 1;
    for (const [aid, mids] of actorMedia) {
      if (mids.size > bestCount) {
        bestCount = mids.size;
        bestActor = aid;
      }
    }

    if (!bestActor) return null;

    const nid = 'a' + bestActor;

    this.hintsUsed += 2; // Bridge hint costs 2
    if (this.settings.hintMode === 'finite') this.hintsRemaining -= 2;

    await this.addNodeToBoard(nid, { isHint: true, hintSource: [...actorMedia.get(bestActor)][0] });
    this._checkWin();
    this._autosave();

    const nodeData = this.board.getNodeData(nid);
    this._emit('hint', { type: 'bridge', nodeId: nid, label: nodeData?.label || '' });
    return nodeData?.label;
  }

  // --- Win Detection ---

  _checkWin() {
    const result = this.board.findBestPath();
    if (!result) {
      this._emit('statsUpdate', this._getStats());
      return;
    }

    const { uniqueMediaCount } = result;
    const isNewBest = !this.bestPath || uniqueMediaCount < this.bestPath;

    if (isNewBest) {
      this.bestPath = uniqueMediaCount;
      this.bestResult = result;
      this.board.highlightBestPath(result);

      const firstWin = !this.won;
      this.won = true;
      this.elapsedTime = Date.now() - this.startTime;

      this._emit('win', { result, firstWin, isNewBest: true });
    }

    this._emit('statsUpdate', this._getStats());
  }

  /** Get Director's Cut — best path from all fetched data */
  getDirectorsCut() {
    const expandedAdj = this.pool.getExpandedAdjacency();
    return findSteinerTree(expandedAdj, this._targetNodeIds);
  }

  /** Graceful give-up — try to find a path in fetched data */
  getGiveUpPath() {
    return this.getDirectorsCut();
  }

  // --- TMDB Fetching ---

  _fetchTmdbForTarget(target) {
    if (typeof target === 'string') {
      const { prefix, numId } = parseNodeId(target);
      return this._fetchTmdb(prefix, numId);
    }
    if (target.type === 'actor') return tmdb(`/person/${target.id}`, { append_to_response: 'combined_credits' });
    if (target.type === 'movie') return tmdb(`/movie/${target.id}`, { append_to_response: 'credits,keywords' });
    if (target.type === 'tv') return tmdb(`/tv/${target.id}`, { append_to_response: 'aggregate_credits' });
    throw new Error('Unknown target type: ' + target.type);
  }

  _fetchTmdb(prefix, numId) {
    if (prefix === 'a') return tmdb(`/person/${numId}`, { append_to_response: 'combined_credits' });
    if (prefix === 'm') return tmdb(`/movie/${numId}`, { append_to_response: 'credits,keywords' });
    if (prefix === 't') return tmdb(`/tv/${numId}`, { append_to_response: 'aggregate_credits' });
    return null;
  }

  _extractNodeInfo(prefix, nodeId, tmdbData) {
    if (prefix === 'a') {
      const credits = tmdbData.combined_credits?.cast || [];
      const relatedNodeIds = credits
        .filter(c => c.media_type === 'movie' || c.media_type === 'tv')
        .map(c => (c.media_type === 'movie' ? 'm' : 't') + c.id);
      return {
        label: tmdbData.name,
        image: imgUrl(tmdbData.profile_path, 'md'),
        genres: [],
        keywords: null,
        relatedNodeIds,
      };
    }
    if (prefix === 'm') {
      const cast = (tmdbData.credits?.cast || []).filter(c => c.profile_path);
      const relatedNodeIds = cast.map(c => 'a' + c.id);
      const kws = (tmdbData.keywords?.keywords || []).map(k => k.name);
      return {
        label: tmdbData.title,
        image: imgUrl(tmdbData.poster_path, 'md'),
        genres: tmdbData.genres?.map(g => g.id) || tmdbData.genre_ids || [],
        keywords: kws,
        relatedNodeIds,
      };
    }
    if (prefix === 't') {
      const cast = (tmdbData.aggregate_credits?.cast || []).filter(c => c.profile_path);
      const relatedNodeIds = cast.map(c => 'a' + c.id);
      return {
        label: tmdbData.name,
        image: imgUrl(tmdbData.poster_path, 'md'),
        genres: tmdbData.genres?.map(g => g.id) || tmdbData.genre_ids || [],
        keywords: null,
        relatedNodeIds,
      };
    }
    return { label: nodeId, image: '', genres: [], keywords: null, relatedNodeIds: [] };
  }

  _expandPool(prefix, tmdbData) {
    if (prefix === 'a') {
      this.pool.expandFromActor(tmdbData);
      const movieCredits = (tmdbData.combined_credits?.cast || []).filter(c => c.media_type === 'movie' && c.title);
      this.pool.fetchAltTitles(movieCredits);
    } else if (prefix === 'm') {
      this.pool.expandFromMovie(tmdbData);
    } else if (prefix === 't') {
      this.pool.expandFromTv(tmdbData);
    }
  }

  _extractYear(tmdbData) {
    if (tmdbData.release_date) return tmdbData.release_date.split('-')[0];
    if (tmdbData.first_air_date) return tmdbData.first_air_date.split('-')[0];
    return '';
  }

  _nodeIdFromTarget(target) {
    if (typeof target === 'string') return target;
    return (target.type === 'actor' ? 'a' : target.type === 'movie' ? 'm' : 't') + target.id;
  }

  // --- Validation ---

  _isBanned(input) {
    // Check against ban list (simplified — full implementation would use slug matching)
    return false;
  }

  _meetsConstraints(match) {
    const pc = this.settings.pathConstraints;
    if (!pc) return true;

    // Genre constraint
    if (pc.genre && match.genre_ids) {
      const genreId = parseInt(pc.genre, 10);
      if (!match.genre_ids.includes(genreId)) return false;
    }

    // Decade constraint
    if (pc.decade) {
      const year = match.release_date?.split('-')[0] || match.first_air_date?.split('-')[0];
      if (year) {
        const decade = Math.floor(parseInt(year, 10) / 10) * 10;
        if (decade !== parseInt(pc.decade, 10)) return false;
      }
    }

    return true;
  }

  // --- Exploration Mode ---

  async _processExplorationGuess(input) {
    // In exploration mode, search TMDB directly and add to board
    try {
      const results = await tmdb('/search/multi', { query: input, include_adult: 'false' });
      const items = results.results || [];

      for (const item of items) {
        let nodeId;
        if (item.media_type === 'person') nodeId = 'a' + item.id;
        else if (item.media_type === 'movie') nodeId = 'm' + item.id;
        else if (item.media_type === 'tv') nodeId = 't' + item.id;
        else continue;

        if (this.board.hasNode(nodeId)) continue;

        const added = await this.addNodeToBoard(nodeId);
        if (added) {
          const result = { success: true, nodeId, label: item.name || item.title, type: item.media_type };
          this.guessHistory.push(result);
          this._emit('guess', result);
          this._checkWin();
          return result;
        }
      }

      const result = { success: false, reason: 'no-match', input };
      this.guessHistory.push(result);
      this._emit('guess', result);
      return result;
    } catch {
      return { success: false, reason: 'error', input };
    }
  }

  // --- Autosave ---

  _autosave() {
    try {
      saveAutosave({
        targets: this.targets,
        boardState: this.board.exportState(),
        hintsUsed: this.hintsUsed,
        won: this.won,
        bestPath: this.bestPath,
        startTime: this.startTime,
        mode: this.settings.scoring,
        timestamp: Date.now(),
      });
    } catch {
      // Non-critical
    }
  }

  // --- Stats ---

  _getStats() {
    const counts = this.board.getCounts();
    return {
      actors: counts.actors,
      titles: counts.titles,
      hintsUsed: this.hintsUsed,
      hintsRemaining: this.hintsRemaining,
      won: this.won,
      bestPath: this.bestPath,
      elapsed: Date.now() - (this.startTime || Date.now()),
    };
  }

  getStats() {
    return this._getStats();
  }

  // --- Event Emitter ---

  on(event, handler) {
    if (!this._listeners.has(event)) this._listeners.set(event, new Set());
    this._listeners.get(event).add(handler);
    return () => this.off(event, handler);
  }

  off(event, handler) {
    const handlers = this._listeners.get(event);
    if (handlers) handlers.delete(handler);
  }

  _emit(event, data) {
    const handlers = this._listeners.get(event);
    if (handlers) {
      for (const handler of handlers) {
        try { handler(data); } catch (e) { console.error('GameController event error:', e); }
      }
    }
  }

  /** Clean up */
  destroy() {
    this.board?.destroy();
    this._listeners.clear();
  }
}
